import React, { useState,useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  LinearProgress,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import {
  Visibility,
  Block,
  CheckCircle,
  Share,
  PersonAdd,
  MoreVert,
  ContentCopy,
  Delete,
  School as CourseIcon,
  AccountTree as LearningPathIcon,Verified,
  AddCircleOutline,RemoveCircleOutline,Favorite,FavoriteBorder
} from "@mui/icons-material";
import { useUser } from "../../context/UserContext";
import { useToast } from "../../context/ToastContext";
import {
  courseService,
  learningPathService,
  jobAidService,
  certificateService,
} from "../../services/api";
import ConfirmDialog from "./ConfirmDialog";
import "../Course/CourseGrid.css";
import ArticleIcon from "@mui/icons-material/Article";
import { tokens } from '../../theme';
import TypeTag from '../shared/TypeTag';

const ItemCard = ({ item, onItemAction,itemType , context="" , className = "", isAdded }) => {
  // Prefer bookmark status provided on the item (server-side) otherwise fall back to cached fetch
  const [bookmarked, setBookmarked] = useState(Boolean(item?.bookmarked || item?.isBookmarked));
  const navigate = useNavigate();
  const { user } = useUser();
  const { showToast } = useToast();
  const theme = useTheme();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    content: "",
    action: null,
  });
  // **ADDED: State for menu anchor**
  const [menuAnchor, setMenuAnchor] = useState(null);
  
  // Determine if we're dealing with a course or learning path
  const isCourse = itemType === "course";
  const isLearningPath = itemType === "learningPath";
  const isCertificate = itemType === "certificate";
  const isJobAid = itemType === "job aid";

  // Get the appropriate service
  const service = isCourse
    ? courseService
    : isLearningPath
    ? learningPathService
    : isCertificate
    ? certificateService
    : jobAidService;

  const titleField = isCourse ? "name" : "title";
  const imagePath = isCourse
    ? "courses"
    : isLearningPath
    ? "learning_path"
    : isCertificate
    ? "certificates"
    : "job-aids";
  const itemTypeName = isCourse
    ? "Course"
    : isLearningPath
    ? "Learning Path"
    : isCertificate
    ? "Certificate"
    : "Job Aid";
  const isLearner = user?.role_name?.toLowerCase() === "learner" || user?.current_role_id === 2;
  const isMyLearningCard = !!item.customButton && !item.isCatalogCard;
  const isCatalogCard = !!item.isCatalogCard;
  const isLearnerCard = isLearner || isMyLearningCard || isCatalogCard;
  // Permission mapping
  const permissions = {
    create: isCourse
      ? "create_course"
      : isLearningPath
      ? "create_learning_path"
      : isCertificate
      ? "create_certificate"
      : "create_job_aids",
    edit: isCourse
      ? "edit_course"
      : isLearningPath
      ? "edit_learning_path"
      : isCertificate
      ? "edit_certificate"
      : "edit_job_aids",
    view: isCourse
      ? "view_course"
      : isLearningPath
      ? "view_learning_path"
      : isCertificate
      ? "view_certificate"
      : "view_job_aids",
    delete: isCourse
      ? "delete_course"
      : isLearningPath
      ? "delete_learning_path"
      : isCertificate
      ? "delete_certificate"
      : "delete_job_aids",
    enroll: isCourse ? "enroll_learners_course" : "enroll_learner_lp",
    disable: isCourse
      ? "disable_course"
      : isLearningPath
      ? "disable_learning_path"
      : isCertificate
      ? "disable_certificate"
      : "disable_job_aids",
  };

  // Check user permissions
  const canCreate = user.permissions?.includes(permissions.create);
  const canEdit = user.permissions?.includes(permissions.edit);
  const canDelete = user.permissions?.includes(permissions.delete);
  const canManageLearners = user.permissions?.includes(permissions.enroll);
  const canDisable = user.permissions?.includes(permissions.disable);
  const canView = user.permissions?.includes(permissions.view);

  const paths = {
    view: isCourse
      ? `/course/preview/${item.id}`
      : isLearningPath
      ? `/learning_path/preview/${item.id}`
      : isCertificate
      ? `/certificates/preview/${item.id}`
      : `/job-aids/preview/${item.id}`,

    edit: isCourse
      ? `/course/form/${item.id}`
      : isLearningPath
      ? `/learning_path/form/${item.id}`
      : isCertificate
      ? `/certificates/update/${item.id}`
      : `/job-aids/edit/${item.id}`,

    enroll: isCourse
      ? `/course/learners/${item.id}`
      : isLearningPath
      ? `/learning_path/learners/${item.id}`
      : "",

    preview: isCourse
      ? `/course/preview/${item.id}`
      : isLearningPath
      ? `/learning_path/preview/${item.id}`
      : isCertificate
      ? `/certificates/preview/${item.id}`
      : `/job-aids/preview/${item.id}`,
  };

  useEffect(() => {
    let isMounted = true;

    // If server already sent bookmark info, use it and skip fetching
    if (typeof item?.bookmarked !== 'undefined' || typeof item?.isBookmarked !== 'undefined') {
      setBookmarked(Boolean(item?.bookmarked || item?.isBookmarked));
    }

    // Do not fetch bookmarks from client-side. Bookmark status must be provided by server in item.bookmarked
    return () => { isMounted = false };
  }, [itemType, item.id, item?.bookmarked, item?.isBookmarked]);

  const toggleBookmark = async (event) => {
    event.stopPropagation(); 

    try {
      if (bookmarked) {
        await courseService.removeBookmark(item.id);
        setBookmarked(false);
        showToast("Bookmark removed successfully", "success");
      } else {
        await courseService.addBookmark(item.id);
        setBookmarked(true);
        showToast("Bookmark added successfully", "success");
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Something went wrong",
      );
    }
  };

  // **ADDED: Menu handlers**
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCopyUrl = () => {
    const url = `${window.location.origin}${paths.preview}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        showToast(`${itemTypeName} URL copied to clipboard!`, "success");
      })
      .catch((err) => {
        showToast("Failed to copy URL", "error");
      });
    handleMenuClose();
  };

  // const handleMenuClick = (type) => {
  //   onAction?.(type, item);
  // };
  const handleEnroll = () => {
    navigate(paths.enroll);
    handleMenuClose();
  };

  const handlePreviewCourse = (course) => {
    const instanceId = course.default_instance_id || 0;
    navigate(`/course/preview/${item.id}`);
  };
  const openConfirmDialog = (type, id, event) => {
    if (event) event.stopPropagation();
    let title = "";
    let content = "";

    switch (type) {
      case "publish":
        title = `Publish ${itemTypeName}`;
        content = `Are you sure you want to publish this ${
          isCourse ? "course" : isLearningPath ? "learning path" : isCertificate ? "certificate" : "Job Aid"
        }? It will be visible to all users.`;
        break;
      case "retire":
        title = `Retire ${itemTypeName}`;
        content = `Are you sure you want to retire this ${
          isCourse ? "course" : isCertificate ? "certificate" : "learning path" 
        }? It will no longer be available for enrollment.`;
        break;
      case "delete":
        title = `Delete ${itemTypeName}`;
        content = `Are you sure you want to delete this ${
          isCourse ? "course" : isLearningPath ? "learning path" : isCertificate ? "certificate" : "Job Aid"
        }? This action cannot be undone.`;
        break;
      case "duplicate":
        title = `Duplicate ${itemTypeName}`;
        content = `Are you sure you want to duplicate this ${
          isCourse ? "course" : isCertificate ? "certificate" : "learning path"
        }? A copy will be created with '(Copy)' added to the title.`;
        break;

      case "withdraw":
        title = `Withdraw ${itemTypeName}`;
        content = `Are you sure you want to withdraw this Job Aid? It will no longer be available to learners.`;
        break;

        case "add_course":
        title = `Add Course ${itemTypeName}`;
        content = `Are you sure you want to add this Course? It will no longer be available to learners.`;
        break;

        case "remove_course":
        title = `Remove Course ${itemTypeName}`;
        content = `Are you sure you want to remove this Course? This action cannot be undone.`;
        break;
        default:
        break;
    }

    setConfirmDialog({
      open: true,
      title,
      content,
      action: () => handleConfirmAction(type, id),
    });
    handleMenuClose();
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, title: "", content: "", action: null });
  };

  const handleConfirmAction = async (type, id) => {
    try {
      
      if (type === "publish") {
        await (isCourse
          ? service.publishCourse(id)
          : isLearningPath
          ? service.publishLearningPath(id)
          : isCertificate
          ? service.publishCertificate(id)
          : service.publishJobAid(id));
        showToast(
          `${isCourse ? "Course" : isLearningPath ? "Learning Path" : isCertificate ? "Certificate" : "Job Aid"} published successfully`,
          "success"
        );
      } else if (type === "retire") {
        await (isCourse
          ? service.retireCourse(id)
          : isCertificate
          ? service.retireCertificate(id)
          : service.retireLearningPath(id));
        showToast(
          `${isCourse ? "Course" : isCertificate ? "Certificate": "Learning Path"} retired successfully`,
          "success"
        );
      } else if (type === "delete") {
        await (isCourse
          ? service.deleteCourse(id)
          : isLearningPath
          ? service.deleteLearningPath(id)
          : isCertificate
          ? service.deleteCertificate(id)
          : service.deleteJobAid(id))
        showToast(
          `${isCourse ? "Course" : isLearningPath ? "Learning Path" : isCertificate ? "Certificate" : "Job Aid"} deleted successfully`,
          "success"
        );
      } else if (type === "duplicate") {
        await (isCourse
          ? service.duplicateCourse(id)
          : isCertificate
          ? service.duplicateCertificate(id)
          : service.duplicateLearningPath(id));
        showToast(
          `${isCourse ? "Course" : isCertificate ? "Certificate" : "Learning Path"} duplicated successfully`,
          "success"
        );
      } /*else if (type === "withdrawn") {
        await (isCourse
          ? service.duplicateCourse(id)
          : isLearningPath
          ? service.duplicateLearningPath(id)
          : service.withdrawnJobAid(id)); 
        showToast(
          `${
            isCourse ? "Course" : "Learning Path"} withdrawn successfully`,
          "success"
        );
      }*/
      else if (type === "withdraw") {
        if (!isCourse && !isLearningPath) {
        await service.withdrawnJobAid(id);
        showToast("Job Aid withdrawn successfully", "success");
      }
      }
      else if (type === "add_course") {
      if (isCourse && context === 'certificate') {
        onItemAction?.(type, item);  
        showToast("Course added to certificate successfully", "success");
      }
    }
    else if (type === "remove_course") {
      if (isCourse && context === "certificate") {
        onItemAction?.(type, item); 
        showToast("Course removed from certificate successfully", "success");
      }
    }

      setTimeout(() => {
        onItemAction?.();
      }, 0);
    } catch (error) {
      console.error(
        `Error ${type} ${
          isCourse ? "course" : isLearningPath ? "learning path" : isCertificate ? "certificate" : "job aid"
        }:`,
        error
      );
      showToast(
        `Failed to ${type} ${
          isCourse ? "course" : isLearningPath ? "learning path" : isCertificate ? "certificate" : "job aid"
        }`,
        "error"
      );
    } finally {
      closeConfirmDialog();
    }
  };

  const getItemIcon = (type) => {
    if (type === "course") return <CourseIcon />;
    if (type === "learningPath") return <LearningPathIcon />;
    if (type === "certificate") return <Verified />;
    if (type === "job Aid") return <ArticleIcon />;
    return null;
  };

  // Get status label
  const getStatusLabel = (status) => {
    if (itemType === "job Aid") {
      return status === 1 ? "Published" : "Withdrawn";
    }
    if (status === "3" || status === 3) return "Published";
    if (status === "2" || status === 2) return "Retired";
    return "Draft";
  };

  const getStatusColor = (status) => {
    if (itemType === "job Aid") {
      return status === 1 ? tokens.color.status.activeText : tokens.color.textTertiary;
    }
    if (status === "3" || status === 3) return tokens.color.status.activeText;  // Published (active green)
    if (status === "2" || status === 2) return tokens.color.textTertiary;  // Retired (muted)
    return tokens.color.textSecondary; // Draft (neutral)
  };
  

  const StatusDot = ({ status }) => {
    const label = getStatusLabel(status);
    if (!label) return null;
    const color = getStatusColor(status);
    return (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <Box component="span" sx={{ width: "7px", height: "7px", borderRadius: "50%", background: color, flexShrink: 0 }} />
        <Typography component="span" sx={{ fontSize: "12px", fontWeight: 500, color, lineHeight: 1 }}>
          {label}
        </Typography>
      </Box>
    );
  };

  const learnerView = isLearnerCard && (
    <div className={`recommendation-card-wrapper ${className}`}>
      <div
        className="recommendation-card"
        //onClick={() => navigate(paths.preview)}
        style={{ position: "relative" }}
      >
        {/* Bookmark stays at the card top-right (mockup keeps the heart). */}
        {itemType === "course" && (
          <IconButton
            onClick={(e) => toggleBookmark(e)}
            className="fav-icon"
            disableRipple
            disableFocusRipple
            sx={{ position: "absolute", top: 10, right: 10, padding: 0, zIndex: 1 }}
          >
            {bookmarked ? (
              <Favorite sx={{ fontSize: "20px", color: tokens.color.status.alertText, transition: "color 0.2s ease", "&:hover": { color: tokens.color.status.alertText } }} />
            ) : (
              <Favorite sx={{ fontSize: "20px", color: "rgba(0, 0, 0, 0.3)", transition: "color 0.2s ease", "&:hover": { color: tokens.color.status.alertText } }} />
            )}
          </IconButton>
        )}
        <div className="recommendation-card-content">
          {/* Row 1 — type tag (+ status when the item carries one). Padded
              right so it never collides with the absolute bookmark. */}
          <Box display="flex" alignItems="center" gap="10px" mb={1.25} pr={itemType === "course" ? "28px" : 0}>
            <TypeTag type={itemTypeName} />
            {item.status !== undefined && <StatusDot status={item.status} />}
          </Box>

          {/* Row 2 — title */}
          <Typography
            variant="subtitle2"
            component="h3"
            className="recommendation-card-title"
            sx={{
              fontWeight: 600,
              pb: 1,
              mb: 1.25,
              borderBottom: "0.5px solid rgba(0,0,0,0.10)",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.3,
            }}
          >
            {item[titleField]}
          </Typography>

          {/* Row 3 — description */}
          <Box
            sx={{
              height: "32px",
              mb: 1,
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            {item.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                className="recommendation-card-desc"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  wordBreak: "break-word",
                  hyphens: "auto",
                }}
                dangerouslySetInnerHTML={{
                  __html: item.description
                    ?.replace(/<\/?p>/g, "")
                    .replace(/<[^>]*>/g, ""),
                }}
              />
            )}
          </Box>

          {/* Row 4 — progress bar with % at right (was a % chip in the tag row) */}
          {item.progress !== undefined &&
            (isMyLearningCard || (isCatalogCard && item.isEnrolled)) && !isJobAid && !isCertificate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.round(item.progress) || 0)}
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 3,
                    backgroundColor: tokens.color.bgTertiary,
                    "& .MuiLinearProgress-bar": {                      
                      backgroundColor: Math.min(100, Math.round(item.progress) || 0) >= 100 ? tokens.color.positive : tokens.color.brand,
                    },
                  }}
                />
                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "text.primary", flexShrink: 0, lineHeight: 1 }}>
                  {Math.round(item.progress)}%
                </Typography>
              </Box>
            )}
          {(isMyLearningCard || isCatalogCard) && (
            <>
              {/* Show custom button for courses and learning paths */}
              {item.customButton && !isJobAid && !isCertificate && (
                <Box
                  sx={{
                  mt: 2,
                  pt: 0,
                  display: "flex",
                  justifyContent: "flex-start", 
                  alignItems: "flex-end",      
                  width: "100%",
                }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      minHeight: "28px",
                      width: "100%",
                    }}
                    onClick={item.customButton.onClick}
                  >
                    {item.customButton.text}
                  </Button>
                </Box>
              )}

              {/* Show download button for Job Aids */}
              {(isMyLearningCard || isCatalogCard) && isJobAid && item.jobAids_url && (

                <Box
                    sx={{ mt: 2,
                      pt: 0,
                      display: "flex",
                      justifyContent: "flex-start", 
                      alignItems: "flex-end",       
                      width: "100%",
                      position: "absolute",         
                      bottom: 12,
                      left: 16,
                    }}
                    >
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    // sx={{ px: 2 }}
                    sx={{px: 1.5,
                        py: 0.5,
                        fontSize: "0.7rem",
                        borderRadius: "4px",
                        minHeight: "28px",
                        minWidth: "auto",
                    }}
                    href={`/uploads/job-aids/${item.jobAids_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    Download
                  </Button>
                </Box>
              )}
            </>
          )}

          {!isMyLearningCard &&
            !isCatalogCard &&
            item.progress !== undefined && isJobAid && (
              <Box mt={1}>
                <LinearProgress
                  variant="determinate"
                  value={item.progress || 0}
                  sx={{
                    height: 8,
                    borderRadius: 3,
                    backgroundColor: tokens.color.bgTertiary,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: (item.progress || 0) >= 100 ? tokens.color.positive : tokens.color.brand,
                    },
                  }}
                />
              </Box>
            )}
        </div>
      </div>
    </div>
  );

  // **UPDATED: Admin view with new card-style layout**
  const adminView = (
    <div className={`admin-card-wrapper ${className}`}>
      <div
        className="admin-card"
      >
        {/* Thumbnail removed — text-forward admin card. */}

        <div className="admin-card-content">
          {/* Row 1 — type tag + status (dot + label), menu at right.
              Mockup order: COURSE · ● Published — tag leads the card. */}
          <Box display="flex" alignItems="center" gap="10px" mb={1.25}>
            <TypeTag type={itemTypeName} />
            <StatusDot status={item.status} />

            <Box sx={{ ml: "auto" }}>
              <IconButton size="small" onClick={handleMenuOpen} >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Row 2 — title */}
          <Typography
            variant="subtitle2"
            component="h3"
            className="admin-card-title"
            sx={{
              fontWeight: 600,
              pb: 1,
              mb: 1.25,
              borderBottom: "0.5px solid rgba(0,0,0,0.10)",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.3,
              minHeight: "50px",
            }}
          >
            {item[titleField]}
          </Typography>

          {/* Row 3 — description */}
          <Box
             sx={{
                  flexGrow: 0,
                }}
          >
            {//{item.description && (
              <Typography
                className="admin-card-desc"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  wordBreak: "break-word",
                  hyphens: "auto",
                  height: "40px",
                }}
                dangerouslySetInnerHTML={{
                  __html: item.description?.replace(/<\/?p>/g, "") || "&nbsp;"
                }}
              />
            }
          </Box>
        </div>
      </div>

      {/* **UPDATED: Material-UI Menu instead of custom dropdown** */}
    <Menu
    anchorEl={menuAnchor}
    open={Boolean(menuAnchor)}
    onClose={handleMenuClose}
    PaperProps={{
      sx: { minWidth: 180 },
    }}
    slotProps={{
      paper: {
        sx: {
          // text color
          '& .MuiListItemText-primary': {
            color: tokens.color.textPrimary,
          },
          // icon color
          '& .MuiListItemIcon-root': {
            color: 'primary.main',
            minWidth: 'auto !important',
          },
        },
      },
    }}
  >
  {context === 'certificate' && itemType === 'course' ? (
    <>
      {isAdded  ? (
      <MenuItem onClick={(e) => openConfirmDialog('remove_course',item.id, e)}>
        <ListItemIcon>
          <RemoveCircleOutline fontSize="small" />
        </ListItemIcon>
        <ListItemText>Remove Course</ListItemText>
      </MenuItem>
    ) : (
      <MenuItem onClick={(e) => openConfirmDialog('add_course',item.id, e)}>
        <ListItemIcon>
          <AddCircleOutline fontSize="small" />
        </ListItemIcon>
        <ListItemText>Add Course</ListItemText>
      </MenuItem>
      )}

      <MenuItem onClick={() => handlePreviewCourse(item.id)}>
        <ListItemIcon>
          <Visibility fontSize="small" />
        </ListItemIcon>
        <ListItemText>Preview Course</ListItemText>
      </MenuItem>
    </>
  ) : (
    <>
      {canView && (
        <MenuItem
          onClick={() => {
            navigate(canEdit && item.status !== 3 && item.status !== '3' && item.status !== 1 ? paths.edit : paths.view);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View {itemTypeName}</ListItemText>
        </MenuItem>
      )}

      {canView && itemType === "job Aid" && item.status === 1 && (
        <MenuItem
          onClick={() => {
            window.open(paths.preview);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview {itemTypeName}</ListItemText>
        </MenuItem>
      )}


     {canView && itemType === "certificate" && item.status === 3 && (
        <MenuItem
          onClick={() => {
            window.open(paths.preview);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview {itemTypeName}</ListItemText>
        </MenuItem>
      )}


      {canManageLearners && (
        <MenuItem onClick={handleEnroll}>
          <ListItemIcon>
            <PersonAdd fontSize="small" />
          </ListItemIcon>
          <ListItemText>Enroll Learners</ListItemText>
        </MenuItem>
      )}

      {canDisable && (
        <>
          {/* Retire if status 3 */}
          {itemType !== "job Aid" &&
            (item.status === 3 || item.status === "3") && (
              <MenuItem onClick={(e) => openConfirmDialog("retire", item.id, e)}>
                <ListItemIcon>
                  <Block fontSize="small" />
                </ListItemIcon>
                <ListItemText>Retire {itemTypeName}</ListItemText>
              </MenuItem>
            )}

          {/* Withdraw Job Aid if status 1 */}
          {itemType === "job Aid" && item.status === 1 && (
            <MenuItem onClick={(e) => openConfirmDialog("withdraw", item.id, e)}>
              <ListItemIcon>
                <Block fontSize="small" />
              </ListItemIcon>
              <ListItemText>Withdraw {itemTypeName}</ListItemText>
            </MenuItem>
          )}

          {/* Publish if not yet published */}
          {itemType !== "job Aid" &&
            item.status !== 3 &&
            item.status !== "3" && (
              <MenuItem onClick={(e) => openConfirmDialog("publish", item.id, e)}>
                <ListItemIcon>
                  <CheckCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>Publish {itemTypeName}</ListItemText>
              </MenuItem>
            )}

          {/* Publish withdrawn Job Aid */}
          {itemType === "job Aid" &&
            getStatusLabel(item.status) === "Withdrawn" && (
              <MenuItem onClick={(e) => openConfirmDialog("publish", item.id, e)}>
                <ListItemIcon>
                  <CheckCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>Publish {itemTypeName}</ListItemText>
              </MenuItem>
            )}
        </>
      )}

      {canCreate && (item.status === "3" || item.status === 3) && (
        <MenuItem onClick={(e) => openConfirmDialog("duplicate", item.id, e)}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate {itemTypeName}</ListItemText>
        </MenuItem>
      )}

      {(item.status === "3" || item.status === 3) && (
        <MenuItem onClick={handleCopyUrl}>
          <ListItemIcon>
            <Share fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
      )}

      {canDelete && item.status !== "3" && item.status !== 3 && (
        <MenuItem onClick={(e) => openConfirmDialog("delete", item.id, e)}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete {itemTypeName}</ListItemText>
        </MenuItem>
      )}
    </>
  )}
</Menu>


      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        content={confirmDialog.content}
        onConfirm={() => {
          if (confirmDialog.action) confirmDialog.action();
        }}
        onCancel={closeConfirmDialog}
      />
    </div>
  );

  return isLearner || isMyLearningCard ? learnerView : adminView;
};

export default ItemCard;
